import { getBackendErrorMessage, getHttpStatus } from "@/core/api/api-error-handler";
import type { ListRequest } from "@/core/api/pagination";
import { LoadScreen } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import type { MerchantDto, MerchantSaleFilter } from "@/features/merchant/api/dto";
import { useMerchantSales } from "@/features/merchant/api/use-merchant-sales";
import { useMerchants } from "@/features/merchant/api/use-merchants";
import { MerchantDetailPage } from "@/routes/merchant/merchant-detail.page";

/**
 * Repli si `POST /Merchants/all` répond 403 (la collection réserve cet
 * endpoint au back-office). `POST /MerchantSales/all` est ouvert au
 * marchand : chaque vente porte `merchantId`.
 */
const OWN_MERCHANT_SALES_LOOKUP: ListRequest<MerchantSaleFilter> = {
  page: 1,
  pageSize: 1,
};

const readSubject = (userInfo: Record<string, unknown> | null): string => {
  const subject = userInfo?.sub;
  return typeof subject === "string" ? subject : "";
};

/** Maître sans parent, sinon le premier identifiant renvoyé. */
const pickOwnMerchantId = (items: MerchantDto[] | undefined): string | undefined => {
  if (!items?.length) return undefined;
  const withId = items.filter((merchant) => Boolean(merchant.id));
  const master = withId.find(
    (merchant) => merchant.merchantType === "MASTER" && !merchant.parentMerchantId
  );
  return (master ?? withId[0])?.id;
};

/**
 * Détail du commerce du marchand connecté (`/app/merchants/me`).
 * Le menu ne fait que naviguer : l'identifiant vient du filtre
 * `ownerKeycloakUserId` (`sub` du jeton) sur `POST /Merchants/all`.
 */
export function MerchantMePage() {
  const { t } = useTypedTranslation();
  const subject = useSessionStore((state) => readSubject(state.userInfo));

  const merchants = useMerchants(
    {
      filter: { ownerKeycloakUserId: subject },
      page: 1,
      pageSize: 20,
    },
    Boolean(subject)
  );
  const merchantsForbidden = getHttpStatus(merchants.error) === 403;
  const sales = useMerchantSales(OWN_MERCHANT_SALES_LOOKUP, merchantsForbidden);

  const merchantId =
    pickOwnMerchantId(merchants.data?.items) ??
    (merchantsForbidden
      ? sales.data?.items?.find((sale) => sale.merchantId)?.merchantId
      : undefined);

  const waiting =
    (Boolean(subject) && merchants.isLoading) ||
    (merchantsForbidden && sales.isLoading);

  if (waiting) {
    return (
      <section className="flex h-64 items-center justify-center">
        <LoadScreen />
      </section>
    );
  }

  if (!merchantId) {
    const failedLookup = merchantsForbidden ? sales : merchants;
    const message =
      failedLookup.isError && !merchantsForbidden
        ? (getBackendErrorMessage(merchants.error) ?? t("merchant.me.loadError"))
        : merchantsForbidden && sales.isError
          ? (getBackendErrorMessage(sales.error) ?? t("merchant.me.loadError"))
          : t("merchant.me.unresolved");

    return (
      <section className="flex flex-col items-center gap-4 py-24">
        <p className="max-w-lg text-center font-semibold text-destructive">
          {message}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            void (merchantsForbidden ? sales.refetch() : merchants.refetch())
          }
        >
          {t("reload")}
        </Button>
      </section>
    );
  }

  return <MerchantDetailPage merchantId={merchantId} />;
}
